import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as env from "shared/env";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "../../add-route/style";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { Dialog } from "component/shared/dialog";
import { CustomAutocomplete } from "component/shared/auto-complete";
import { LeadSourceType } from "type/lead-source-type";
import { TriggerType } from "type/trigger-type";
import { getCampaignRoutes } from "redux/selector";
import { getActiveDripCampaigns } from "redux/selector";
import { getLeadsOptimized } from "redux/selector"
import { CampaignRoute } from "model/campaign-route";
import { createCampaignRoute, updateCampaignRoute } from "redux/slice/campaign-routes";
import * as yup from "yup";
import { validate } from "shared/yup";

interface OwnProps {
	open: boolean,
	edit?: boolean,
	campaignRoute?: CampaignRoute,
	onClose: () => void,
}

interface Props extends OwnProps, WithStyles<typeof styles> {}


interface CampaignOptions {
	id: number,
	label: string,
}

const AddRouteComponent: React.FC<Props> = ({ open, edit, campaignRoute, onClose, classes = {} }) => {
	const dispatch = useAppThunkDispatch();
	
	// Redux selectors
	const campaignRoutes = useSelector(getCampaignRoutes);
	const campaigns = useSelector(getActiveDripCampaigns);
	const leads = useSelector(getLeadsOptimized);

	// Local state
	const [leadSourceInput, setLeadSourceInput] = useState<string>("");
	const [campaignInput, setCampaignInput] = useState<string>("");
	const [campaignId, setCampaignId] = useState<number | undefined>(undefined);
	const [inputsUpdated, setInputsUpdated] = useState<boolean>(false);
	const [submitted, setSubmitted] = useState<boolean>(false);

	// Check if there's already a default route
	const hasDefault = useMemo(() => {
		return campaignRoutes.some(route => !route.leadSource);
	}, [campaignRoutes]);

	// Validation schema
	const validationSchema = useMemo(() => ({
		leadSourceInput: yup
			.string()
			.test(
				"check-lead-source",
				"Please select lead source.", 
				() => {
					// only require if there is already default
					let result = true;
					if (hasDefault) {
						if (!leadSourceInput) {
							result = false;
						}
					}
					return result;
				}
			),
		campaignInput: yup.string().required("Please choose a campaign"),
	}), [hasDefault, leadSourceInput]);

	// Lead source options
	const leadSourceOptions = useMemo(() => {
		const leadSources = new Set(LeadSourceType.values().map(value => value.label));
		campaignRoutes.forEach((campaignRoute) => {
			if (campaignRoute.leadSource?.startsWith('IHF_')) return;
			if (!LeadSourceType.getById(campaignRoute.leadSource ? campaignRoute.leadSource : "")) {
				campaignRoute.leadSource && leadSources.add(campaignRoute.leadSource)
			}
		});
		leads.forEach((lead) => lead.source && leadSources.add(lead.source));
		return Array.from(leadSources).sort((a, b) => {
			return a.toLowerCase().localeCompare(b.toLowerCase());
		});
	}, [campaignRoutes, leads]);

	// Campaign options
	const campaignOptions = useMemo(() => {
		const campaignOptions: CampaignOptions[] = campaigns.map(campaign => ({
			id: campaign.id,
			label: campaign.name,
		})).filter(Boolean) as CampaignOptions[];
		return campaignOptions;
	}, [campaigns]);

	// Get campaign by ID
	const getCampaign = useCallback((campaignId: number) => {
		return campaigns.find((campaign) => campaign.id === campaignId);
	}, [campaigns]);

	// Reset form
	const resetForm = useCallback((updated?: boolean) => {
		setCampaignInput("");
		setLeadSourceInput("");
		setInputsUpdated(updated ? updated : false);
		setCampaignId(undefined);
		setSubmitted(false);
	}, []);

	// Set existing routes when editing
	const setExistingRoutes = useCallback(() => {
		if (campaignRoute && !inputsUpdated) {
			const knownSource = LeadSourceType.getById(campaignRoute.leadSource ?? "");
			const displaySource = knownSource?.label
				|| (campaignRoute.leadSource?.startsWith('IHF_') ? "" : (campaignRoute.leadSource ?? ""));
			setLeadSourceInput(displaySource);
			setCampaignInput(campaignRoute.campaign?.name || '');
			setCampaignId(campaignRoute.campaign?.id);
			setInputsUpdated(true);
		}
		if (!campaignRoute && !inputsUpdated) {
			resetForm(true);
		}
	}, [campaignRoute, inputsUpdated, resetForm]);

	// Handle dialog open/close effects
	useEffect(() => {
		if (open) {
			setExistingRoutes();
		} else {
			resetForm();
		}
	}, [open, setExistingRoutes, resetForm]);

	const syncFormNewLeadRoute = useCallback((campaign: ReturnType<typeof getCampaign>) => {
		if (!campaign) return;
		const existing = campaignRoutes.find(
			(r) => Array.isArray(r.triggers) && r.triggers.includes(TriggerType.FORM_NEW_LEAD.id)
		);
		if (existing) {
			dispatch(updateCampaignRoute({ campaignRoute: {
				...existing,
				campaign,
			}}));
		} else {
			dispatch(createCampaignRoute({ campaignRoute: {
				campaign,
				triggers: [TriggerType.FORM_NEW_LEAD.id, TriggerType.UNMATCHED_LEAD_SOURCE.id],
				enrollmentMode: "ONE_TIME",
				inactive: false,
			}}));
		}
	}, [campaignRoutes, dispatch]);

	// Save function
	const save = useCallback(() => {
		const state = { leadSourceInput, campaignInput, campaignId };
		const errors = validate(validationSchema, state);
		const campaign = campaignId && getCampaign(campaignId);

		if (!errors && campaign) {
			let leadSource = LeadSourceType.getByLabel(leadSourceInput ? leadSourceInput : "")?.id;
			if (!leadSource && leadSourceInput !== "") {
				leadSource = leadSourceInput;
			}
			const isDefaultRoute = !leadSource;

			if (campaignRoute && campaignRoute.id) {
				const id = campaignRoute.id;
				dispatch(updateCampaignRoute({ campaignRoute: {
					id,
					campaign,
					leadSource,
					triggers: leadSource ? [leadSource] : (campaignRoute.triggers || []),
					enrollmentMode: campaignRoute.enrollmentMode || 'ONE_TIME',
					inactive: campaignRoute.inactive || false,
				}}));
			} else if (!isDefaultRoute) {
				dispatch(createCampaignRoute({ campaignRoute: {
					campaign,
					leadSource,
					triggers: leadSource ? [leadSource] : [],
					enrollmentMode: 'ONE_TIME',
					inactive: false,
				}}));
			}
			if (isDefaultRoute) {
				syncFormNewLeadRoute(campaign);
			}
			close();
		}
	}, [leadSourceInput, campaignInput, campaignId, validationSchema, getCampaign, campaignRoute, dispatch, syncFormNewLeadRoute]);

	// Close function
	const close = useCallback(() => {
		onClose();
		setSubmitted(false);
	}, [onClose]);
	// Calculate errors for current state
	const state = { leadSourceInput, campaignInput, campaignId };
	const errors = validate(validationSchema, state);

	return (
		<Dialog
			open={open} 
			onClose={close} 
			scroll="paper" 
			fullWidth={true} maxWidth="md" 
			classes={{scrollPaper: classes.scrollPaper }}
		>
			<Mui.DialogTitle>
				<Mui.Grid container direction="column" spacing={1}>
					<Mui.Grid item xs={12}>{edit ? "Edit" : "Add A"} Rule</Mui.Grid>
				</Mui.Grid>
			</Mui.DialogTitle>
			<Mui.DialogContent>
				<Mui.Grid container direction="column" spacing={2}>
					<Mui.Grid item>
						<CustomAutocomplete
							options={leadSourceOptions}
							optionsDisabled={campaignRoutes
								.filter(campaignRoute => !campaignRoute.leadSource?.startsWith('IHF_'))
								.map(campaignRoute => {
									const knownLeadSource = LeadSourceType.getById(campaignRoute.leadSource ? campaignRoute.leadSource : "")?.label
									if (knownLeadSource) {
										return knownLeadSource
									} else {
										return campaignRoute.leadSource;
									}
								})}
							textInputHeight={true}
							label="If source is:"
							optionLabelExtractor={(option) => option}
							fullWidth
							margin="dense"
							placeholder={hasDefault ? "Select a source" : "Default (website leads & leads with no external source)"}
							onInputChange={(event, value, reason) => {
								if (inputsUpdated) {
									setLeadSourceInput(value);
								}
							}}
							onChange={(event, option) => {
								if (option && option.label) {
									setLeadSourceInput(option.label);
								}
							}}
							inputValue={leadSourceInput}
							helperText={submitted && errors && errors.leadSourceInput}
							error={submitted && errors && !!errors.leadSourceInput}
							onFocus={(event) => {
								setLeadSourceInput("");
							}}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<CustomAutocomplete
							options={campaignOptions}
							textInputHeight={true}
							label="Assign to:"
							optionLabelExtractor={(option) => option.label}
							fullWidth
							margin="dense"
							placeholder="Select a campaign"
							onInputChange={(event, value, reason) => {
								if (inputsUpdated) {
									setCampaignInput(value);
								}
							}}
							onChange={(event, option) => {
								if (option && option.label) {
									setCampaignId(option.id);
								}
							}}
							inputValue={campaignInput}
							helperText={submitted && errors && errors.campaignInput}
							error={submitted && errors && !!errors.campaignInput}
							onFocus={(event) => {
								setCampaignInput("");
							}}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Button
							variant="outlined"
							color={"primary"}
							onClick={close} 
							className={classes.cancel}
						>
							Cancel
						</Mui.Button>
						<Mui.Button
							variant="contained"
							color={"primary"}
							onClick={() => {
								setSubmitted(true);
								save();
							}}
						>
							Save
						</Mui.Button>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.DialogContent>
		</Dialog>
	);
};

export const AddRoute = withStyles(AddRouteComponent, styles);